import * as React from "react";
import gql from "graphql-tag";
import { graphql, compose } from "react-apollo";
import { groupAuth } from "app/utils/authGroups";

import oktaClient from "app/utils/oktaClient";
import handleMutationError from "app/utils/handleMutationError";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core";

const muiTheme = createMuiTheme({
  overrides: {
    MuiFormControl: {
      fullWidth: {
        //width = (full width - adornment padding-left)
        width: "calc(100% - 12px)",
      },
    },
  },
  typography: {
    useNextVariants: true,
  },
});

class Basic extends React.Component<any, any> {
  constructor(props: any) {
    super(props);

    this.state = {
      refundOpen: false,
      requestValue: "",
      requestHelperText: "",
      requestError: false,
      confirmValue: "",
      confirmHelperText: "",
      confirmError: false,
      comments: "",
    };

    this.executeGql = this.executeGql.bind(this);
    this.finalizePayment = this.finalizePayment.bind(this);
    this.cancelPayment = this.cancelPayment.bind(this);
    this.failPayment = this.failPayment.bind(this);

    this.sendPopEmail = this.sendPopEmail.bind(this);
    this.downloadPop = this.downloadPop.bind(this);
    this.requestRefund = this.requestRefund.bind(this);

    this.openRefund = this.openRefund.bind(this);
    this.closeRefund = this.closeRefund.bind(this);
    this.requestValidation = this.requestValidation.bind(this);

    this.handleCommentsChange = this.handleCommentsChange.bind(this);
  }

  executeGql(
    gqlFunction: Function,
    params: { [key: string]: string | number },
    message: string
  ) {
    gqlFunction({ variables: params })
      .then(() => {
        this.props.loadData();

        if (message) {
          alert(message);
        }
      })
      .catch(handleMutationError);
  }

  finalizePayment(reference: string) {
    this.executeGql(this.props.finalize_payment, { reference: reference }, "");
  }

  cancelPayment(reference: string) {
    this.executeGql(this.props.cancel_payment, { reference: reference }, "");
  }

  failPayment(reference: string) {
    this.executeGql(
      this.props.fail_pending_payment,
      { reference: reference },
      ""
    );
  }

  sendPopEmail(reference: string) {
    this.executeGql(
      this.props.send_pop_email,
      { reference: reference },
      "Your email will be sent!"
    );
  }

  downloadPop(receiptUrl: string) {
    const newWindow = window.open(receiptUrl, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  }

  requestRefund(reference: string, amount: number, comments: string) {
    this.executeGql(
      this.props.request_refund,
      {
        reference: reference,
        amount: amount,
        comments: comments,
        user_email: oktaClient.getToken().claims.email,
      },
      `Refund of R${(amount / 100).toFixed(2)} has been requested!`
    );
  }

  openRefund() {
    this.setState({ refundOpen: true });
  }

  closeRefund() {
    this.setState({ refundOpen: false });
  }

  requestValidation() {
    this.setState({
      requestError: false,
      requestHelperText: "",
      confirmError: false,
      confirmHelperText: "",
    });

    let bail = false;

    if (this.state.requestValue !== this.state.confirmValue) {
      this.setState({
        confirmError: true,
        confirmHelperText: "Amounts do not match.",
      });
      bail = true;
    }
    if (
      parseFloat(this.state.requestValue) >
      this.props?.data?.payable_amount / 100
    ) {
      this.setState({
        requestError: true,
        requestHelperText: "Amount exceeds total payment value.",
      });
      bail = true;
    }

    if (!bail) {
      this.requestRefund(
        this.props.data.reference,
        parseFloat(this.state.requestValue) * 100,
        this.state.comments
      );
      this.closeRefund();
    }
  }

  handleCommentsChange = (e) => {
    this.setState({ comments: e.target.value });
  };

  render() {
    const payment = this.props.data;

    return (
      <div>
        <table
          style={{
            fontSize: "14px",
            paddingTop: "10px",
            width: "100%",
          }}
          cellPadding={0}
          cellSpacing={0}
        >
          <tbody>
            <tr>
              <td colSpan={4}>
                <h3
                  style={{
                    borderBottom: "1px solid rgb(204, 204, 204)",
                    fontWeight: 400,
                    display: "flex",
                    alignItems: "center",
                    paddingBottom: "10px",
                  }}
                >
                  <span style={{ flex: 1 }}>Basic</span>

                  {/* Send POP Email Button */}
                  <button
                    className={`button ${
                      payment.status === "completed" ? "" : "hidden"
                    }`}
                    onClick={() => this.sendPopEmail(payment.reference)}
                  >
                    Email POP
                  </button>

                  {/* Download POP Button */}
                  <button
                    className={`button ${
                      payment.status === "completed" ? "" : "hidden"
                    }`}
                    onClick={() => this.downloadPop(payment.receipt_url)}
                  >
                    View/Download POP
                  </button>

                  {/* Refund Request Button */}
                  <button
                    className={`button
                    ${
                      payment.refundable &&
                      groupAuth.hasAccessGroup(["Fraud/Admin"])
                        ? ""
                        : "hidden"
                    }`}
                    onClick={this.openRefund}
                  >
                    Request Refund
                  </button>

                  {/* Finalize Payment Button */}
                  <button
                    className={`button ${
                      ["reserved", "finalize_failed"].includes(
                        payment.pending_status
                      )
                        ? ""
                        : "hidden"
                    }`}
                    onClick={() => this.finalizePayment(payment.reference)}
                  >
                    Finalize Payment
                  </button>

                  {/* Cancel Payment Button */}
                  <button
                    className={`button ${
                      !!payment.cancelable ||
                      payment.pending_status === "cancel_failed"
                        ? ""
                        : "hidden"
                    }`}
                    onClick={() => this.cancelPayment(payment.reference)}
                  >
                    Cancel Payment
                  </button>

                  {/* Fail Payment Button */}
                  <button
                    className={`button ${
                      this.props.techops && payment.status === "pending"
                        ? ""
                        : "hidden"
                    }`}
                    onClick={() => this.failPayment(payment.reference)}
                  >
                    Fail Payment
                  </button>
                </h3>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Date:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {" "}
                {this.props.formatDate(payment.created_at)}{" "}
              </td>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Reference:</b>
              </td>
              <td style={{ padding: "5px" }}> {payment.reference} </td>
            </tr>
            <tr>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Status:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {" "}
                {this.props.formatStatus(payment.status)}{" "}
              </td>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Pending status:</b>
              </td>
              <td style={{ padding: "5px" }}> {payment.pending_status} </td>
            </tr>
            <tr>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Merchant:</b>
              </td>
              <td style={{ padding: "5px" }}> {payment.merchant.name} </td>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Payable amount:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {" "}
                R{(parseInt(payment.payable_amount, 10) / 100).toFixed(2)}{" "}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>PSP:</b>
              </td>
              <td style={{ padding: "5px" }}> {payment.psp} </td>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Amount (Merchant):</b>
              </td>
              <td style={{ padding: "5px" }}>
                {" "}
                R{(parseInt(payment.merchant_amount, 10) / 100).toFixed(2)}{" "}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>PSP reference:</b>
              </td>
              <td style={{ padding: "5px" }}> {payment.psp_reference} </td>
              <td style={{ padding: "5px", paddingLeft: 0 }}>
                <b style={{ fontWeight: 400 }}>Service fee:</b>
              </td>
              <td style={{ padding: "5px" }}>
                {" "}
                R{(parseInt(payment.user_fee_amount, 10) / 100).toFixed(2)}{" "}
              </td>
            </tr>
          </tbody>
        </table>

        <Dialog open={this.state.refundOpen} onClose={this.closeRefund}>
          <DialogTitle>Request refund</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the amount to refund from the total of R
              {(payment.payable_amount / 100).toFixed(2)}
            </DialogContentText>
            <MuiThemeProvider theme={muiTheme}>
              <TextField
                required
                id="amount"
                label="Refund Amount"
                type="number"
                inputProps={{
                  step: ".01",
                  min: "0",
                  max: payment.payable_amount / 100,
                  list: "defaultNumbers",
                }}
                error={this.state.requestError}
                helperText={this.state.requestHelperText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R</InputAdornment>
                  ),
                }}
                margin="normal"
                variant="filled"
                fullWidth
                onChange={() => {
                  if (this.state.requestError)
                    this.setState({
                      requestError: false,
                      requestHelperText: "",
                    });
                }}
                onBlur={(e) =>
                  this.setState({
                    requestValue: e.target.value,
                  })
                }
              />
              <TextField
                required
                id="amount-confirm"
                label="Confirm Amount"
                type="number"
                inputProps={{
                  step: ".01",
                  min: "0",
                  max: payment.payable_amount / 100,
                }}
                error={this.state.confirmError}
                helperText={this.state.confirmHelperText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R</InputAdornment>
                  ),
                }}
                margin="normal"
                variant="filled"
                fullWidth
                onChange={() => {
                  if (this.state.confirmError)
                    this.setState({
                      confirmError: false,
                      confirmHelperText: "",
                    });
                }}
                onBlur={(e) =>
                  this.setState({
                    confirmValue: e.target.value,
                  })
                }
              />
              <datalist id="defaultNumbers">
                <option value={(payment.payable_amount / 100).toFixed(2)} />
              </datalist>

              <TextField
                fullWidth
                margin="normal"
                variant="filled"
                label="Comments"
                id="request-refund-comments"
                value={this.state.comments}
                onChange={this.handleCommentsChange}
              />
            </MuiThemeProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeRefund}>Cancel</Button>
            <Button onClick={this.requestValidation}>Request</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

// QUERIES
const sendPopEmail = graphql(
  gql`
    mutation email_pop($reference: String!) {
      send_pop_email(reference: $reference) {
        reference
      }
    }
  `,
  {
    name: "send_pop_email",
  }
);
const requestRefund = graphql(
  gql`
    mutation request_refund(
      $reference: String!
      $amount: Int!
      $comments: String!
      $user_email: String!
    ) {
      request_refund(
        reference: $reference
        amount: $amount
        comments: $comments
        user_email: $user_email
      ) {
        reference
      }
    }
  `,
  {
    name: "request_refund",
  }
);
const finalizePayment = graphql(
  gql`
    mutation finalize_payment($reference: String!) {
      finalize_payment(reference: $reference) {
        status
      }
    }
  `,
  {
    name: "finalize_payment",
  }
);
const cancelPayment = graphql(
  gql`
    mutation cancel_payment($reference: String!) {
      cancel_payment(reference: $reference) {
        status
      }
    }
  `,
  {
    name: "cancel_payment",
  }
);
const failPayment = graphql(
  gql`
    mutation fail_pending_payment($reference: String!) {
      fail_pending_payment(reference: $reference) {
        status
      }
    }
  `,
  {
    name: "fail_pending_payment",
  }
);

export default compose<any>(
  sendPopEmail,
  requestRefund,
  failPayment,
  cancelPayment,
  finalizePayment
)(Basic);
